import { useState, useRef } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

// Section metadata registry - maps section IDs to display info
export const SECTION_REGISTRY = {
    tablesManagement:          { icon: '🏛️', labelKey: 'tablesManagement' },
    userManagement:            { icon: '👥', labelKey: 'userManagement' },
    activeSessionsManagement:  { icon: '👥', labelKey: 'activeSessionsManagement' },
    registerManagement:        { icon: '💰', labelKey: 'registerManagement' },
    productManagement:         { icon: '🛍️', labelKey: 'productManagement' },
    optionManagement:          { icon: '⚙️', labelKey: 'optionManagement' },
    specialsManagement:        { icon: '🎁', labelKey: 'specialsManagement' },
    tipAnalysis:               { icon: '💳', labelKey: 'creditTips' },
    outboundInvoices:          { icon: '📤', labelKey: 'outboundInvoices' },
    inboundInvoices:           { icon: '📥', labelKey: 'inboundInvoices' },
    beerProduction:            { icon: '🍺', labelKey: 'beerProduction' },
    inventoryManagement:       { icon: '📦', labelKey: 'inventoryManagement' },
    iepsCalculator:            { icon: '📊', labelKey: 'iepsCalculator' },
    quickStats:                { icon: '💰', labelKey: 'sales' },
    advancedAnalytics:         { icon: '📊', labelKey: 'advancedAnalytics' },
    digitalMenu:               { icon: '📱', labelKey: 'digitalMenu' },
    systemSettings:            { icon: '⚙️', labelKey: 'systemSettings' },
    printerManagement:         { icon: '🖨️', labelKey: 'printerManagement' },
    themeManagement:           { icon: '🎨', labelKey: 'themeManagement' },
    adminLayout:               { icon: '🧩', labelKey: 'adminLayout' }
};

// Default layout matching the original hardcoded structure
export const DEFAULT_LAYOUT = {
    tabs: [
        { id: 'setup', icon: '🏗️', labelKey: 'restaurantSetup', visible: true, sections: [
            { id: 'tablesManagement', visible: true },
            { id: 'userManagement', visible: true },
            { id: 'activeSessionsManagement', visible: true },
            { id: 'registerManagement', visible: true },
            { id: 'productManagement', visible: true },
            { id: 'optionManagement', visible: true },
            { id: 'specialsManagement', visible: true }
        ]},
        { id: 'finance', icon: '💰', labelKey: 'financeInvoicing', visible: true, sections: [
            { id: 'tipAnalysis', visible: true },
            { id: 'outboundInvoices', visible: true },
            { id: 'inboundInvoices', visible: true },
            { id: 'beerProduction', visible: true },
            { id: 'inventoryManagement', visible: true },
            { id: 'iepsCalculator', visible: true }
        ]},
        { id: 'analytics', icon: '📊', labelKey: 'analytics', visible: true, sections: [
            { id: 'quickStats', visible: true },
            { id: 'advancedAnalytics', visible: true }
        ]},
        { id: 'digital-menu', icon: '📱', labelKey: 'digitalMenu', visible: true, sections: [
            { id: 'digitalMenu', visible: true }
        ]},
        { id: 'settings', icon: '⚙️', labelKey: 'systemSettings', visible: true, sections: [
            { id: 'systemSettings', visible: true },
            { id: 'adminLayout', visible: true },
            { id: 'printerManagement', visible: true },
            { id: 'themeManagement', visible: true }
        ]}
    ]
};

export default function AdminLayoutConfigurator({ theme, layout, onLayoutChange }) {
    const { t } = useLanguage();
    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState('');
    const dragItem = useRef(null);
    const [dragOverTab, setDragOverTab] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const handleDragStart = (e, tabId, sectionIndex) => {
        dragItem.current = { tabId, sectionIndex };
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', ''); // Required for Firefox
    };

    const handleDragOver = (e, tabId, dropIndex) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverTab(tabId);
        setDragOverIndex(dropIndex);
    };

    const handleDragLeave = () => {
        setDragOverTab(null);
        setDragOverIndex(null);
    };

    const handleDrop = (e, targetTabId, targetIndex) => {
        e.preventDefault();
        setDragOverTab(null);
        setDragOverIndex(null);

        if (!dragItem.current) return;
        const { tabId: sourceTabId, sectionIndex: sourceIndex } = dragItem.current;
        dragItem.current = null;

        const newLayout = JSON.parse(JSON.stringify(layout));
        const sourceTab = newLayout.tabs.find(tab => tab.id === sourceTabId);
        const targetTab = newLayout.tabs.find(tab => tab.id === targetTabId);
        if (!sourceTab || !targetTab) return;

        // Remove from source
        const [movedSection] = sourceTab.sections.splice(sourceIndex, 1);

        // Insert at target position
        const insertAt = targetIndex !== undefined && targetIndex !== null ? targetIndex : targetTab.sections.length;
        targetTab.sections.splice(insertAt, 0, movedSection);

        onLayoutChange(newLayout);
    };

    const handleDragEnd = () => {
        dragItem.current = null;
        setDragOverTab(null);
        setDragOverIndex(null);
    };

    const toggleTabVisibility = (tabId) => {
        // Settings tab can't be hidden (contains this configurator)
        if (tabId === 'settings') return;
        const newLayout = JSON.parse(JSON.stringify(layout));
        const tab = newLayout.tabs.find(t => t.id === tabId);
        if (tab) tab.visible = !tab.visible;
        onLayoutChange(newLayout);
    };

    const toggleSectionVisibility = (tabId, sectionIndex) => {
        // adminLayout section can't be hidden
        const tab = layout.tabs.find(t => t.id === tabId);
        if (tab?.sections[sectionIndex]?.id === 'adminLayout') return;
        const newLayout = JSON.parse(JSON.stringify(layout));
        const targetTab = newLayout.tabs.find(t => t.id === tabId);
        if (targetTab) targetTab.sections[sectionIndex].visible = !targetTab.sections[sectionIndex].visible;
        onLayoutChange(newLayout);
    };

    const handleReset = () => {
        onLayoutChange(JSON.parse(JSON.stringify(DEFAULT_LAYOUT)));
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveStatus('');
        try {
            const response = await fetch(`${BACKEND_URL}/api/system-settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminLayout: JSON.stringify(layout) })
            });
            if (!response.ok) throw new Error('Failed to save');
            setSaveStatus('saved');
        } catch {
            setSaveStatus('error');
        } finally {
            setSaving(false);
            setTimeout(() => setSaveStatus(''), 3000);
        }
    };

    return (
        <div style={{ padding: '1rem' }}>
            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                        padding: '0.5rem 1.2rem',
                        backgroundColor: theme?.colors?.success || '#4caf50',
                        color: theme?.colors?.textLight || '#fff',
                        border: 'none',
                        borderRadius: theme?.borderRadius || 4,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.9rem'
                    }}
                >
                    {saving ? '...' : t('save')}
                </button>
                <button
                    onClick={handleReset}
                    style={{
                        padding: '0.5rem 1.2rem',
                        backgroundColor: theme?.colors?.error || '#e74c3c',
                        color: theme?.colors?.textLight || '#fff',
                        border: 'none',
                        borderRadius: theme?.borderRadius || 4,
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.9rem'
                    }}
                >
                    {t('resetToDefault') || 'Reset to Default'}
                </button>
                {saveStatus === 'saved' && (
                    <span style={{ color: theme?.colors?.success || '#4caf50', fontWeight: 'bold', fontSize: '0.85rem' }}>
                        {t('saved') || 'Saved!'}
                    </span>
                )}
                {saveStatus === 'error' && (
                    <span style={{ color: theme?.colors?.error || '#e74c3c', fontWeight: 'bold', fontSize: '0.85rem' }}>
                        {t('error')}
                    </span>
                )}
            </div>

            <p style={{ color: theme?.colors?.textSecondary || '#666', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>
                {t('adminLayoutInfo') || 'Drag sections between tabs to reorganize. Toggle visibility with the eye icon.'}
            </p>

            {/* Tab columns */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${layout.tabs.length}, 1fr)`,
                gap: '0.75rem',
                overflowX: 'auto'
            }}>
                {layout.tabs.map(tab => {
                    const isTabHidden = !tab.visible;
                    const isDragTarget = dragOverTab === tab.id;
                    return (
                        <div
                            key={tab.id}
                            onDragOver={(e) => handleDragOver(e, tab.id, tab.sections.length)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, tab.id, dragOverIndex !== null ? dragOverIndex : tab.sections.length)}
                            style={{
                                border: `2px solid ${isDragTarget ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.border || '#ccc')}`,
                                borderRadius: theme?.borderRadius || 8,
                                backgroundColor: isDragTarget
                                    ? `${theme?.colors?.primary || '#1976d2'}11`
                                    : (theme?.colors?.background || '#f5f5f5'),
                                opacity: isTabHidden ? 0.5 : 1,
                                minHeight: '120px',
                                transition: 'border-color 0.15s, background-color 0.15s'
                            }}
                        >
                            {/* Tab header */}
                            <div style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme?.colors?.primary || '#1976d2',
                                color: theme?.colors?.surface || '#fff',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontWeight: 'bold',
                                fontSize: '0.85rem',
                                borderRadius: `${(theme?.borderRadius || 8) - 2}px ${(theme?.borderRadius || 8) - 2}px 0 0`
                            }}>
                                <span>{tab.icon} {t(tab.labelKey)}</span>
                                <button
                                    onClick={() => toggleTabVisibility(tab.id)}
                                    disabled={tab.id === 'settings'}
                                    title={tab.id === 'settings' ? 'Settings tab cannot be hidden' : (tab.visible ? 'Hide tab' : 'Show tab')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: theme?.colors?.surface || '#fff',
                                        cursor: tab.id === 'settings' ? 'not-allowed' : 'pointer',
                                        fontSize: '1rem',
                                        padding: '0 0.25rem',
                                        opacity: tab.id === 'settings' ? 0.4 : 1
                                    }}
                                >
                                    {tab.visible ? '👁️' : '🚫'}
                                </button>
                            </div>

                            {/* Section cards */}
                            <div style={{ padding: '0.4rem', minHeight: '60px' }}>
                                {tab.sections.map((section, idx) => {
                                    const meta = SECTION_REGISTRY[section.id] || { icon: '?', labelKey: section.id };
                                    const isDropTarget = isDragTarget && dragOverIndex === idx;
                                    return (
                                        <div key={section.id}>
                                            {isDropTarget && (
                                                <div style={{
                                                    height: '3px',
                                                    backgroundColor: theme?.colors?.primary || '#1976d2',
                                                    borderRadius: '2px',
                                                    margin: '0.15rem 0'
                                                }} />
                                            )}
                                            <div
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, tab.id, idx)}
                                                onDragOver={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setDragOverTab(tab.id);
                                                    setDragOverIndex(idx);
                                                }}
                                                onDrop={(e) => {
                                                    e.stopPropagation();
                                                    handleDrop(e, tab.id, idx);
                                                }}
                                                onDragEnd={handleDragEnd}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.4rem',
                                                    padding: '0.35rem 0.5rem',
                                                    margin: '0.2rem 0',
                                                    backgroundColor: section.visible
                                                        ? (theme?.colors?.surface || '#fff')
                                                        : `${theme?.colors?.surface || '#fff'}88`,
                                                    border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                                    borderRadius: theme?.borderRadius || 4,
                                                    cursor: 'grab',
                                                    fontSize: '0.8rem',
                                                    opacity: section.visible ? 1 : 0.45,
                                                    color: theme?.colors?.text || '#000',
                                                    userSelect: 'none'
                                                }}
                                            >
                                                <span style={{ cursor: 'grab', fontSize: '0.7rem', color: theme?.colors?.textSecondary || '#999' }}>☰</span>
                                                <span style={{ flex: 1 }}>{meta.icon} {t(meta.labelKey)}</span>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); toggleSectionVisibility(tab.id, idx); }}
                                                    disabled={section.id === 'adminLayout'}
                                                    title={section.id === 'adminLayout' ? 'Cannot hide layout configurator' : ''}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: section.id === 'adminLayout' ? 'not-allowed' : 'pointer',
                                                        fontSize: '0.75rem',
                                                        padding: '0 0.15rem',
                                                        opacity: section.id === 'adminLayout' ? 0.4 : 1
                                                    }}
                                                >
                                                    {section.visible ? '👁️' : '🚫'}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
